import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

/**
 * A custom element that displays the delivery-time message for the selected variant.
 *
 * The message depends on per-variant inventory, so it has to be recomputed on every
 * variant change. Rather than duplicating the Liquid rules in JS, this swaps in the
 * server-rendered markup for the new variant, which keeps the metafield lookups and the
 * state resolution in one place (snippets/product-availability-state.liquid).
 */
class ProductDelivery extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateDelivery);
    // Delegated so it survives the morph below, which replaces every child.
    this.addEventListener('click', this.handleNotifyClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateDelivery);
    this.removeEventListener('click', this.handleNotifyClick);
  }

  /**
   * Opens the back-in-stock form by triggering the app's own button, so the app keeps
   * owning the form, its variant context and its submissions. The button is injected
   * asynchronously and only for variants the app covers, hence the lookup on each click
   * rather than a cached reference.
   * @param {MouseEvent} event
   */
  handleNotifyClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-delivery-notify]')) return;

    event.preventDefault();
    /** @type {HTMLElement | null} */
    const appButton = document.querySelector('.rez-stock-notification-button');
    appButton?.click();
  };

  /**
   * Updates the delivery message.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateDelivery = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newDelivery = event.detail.data.html.querySelector('product-delivery');

    if (!newDelivery) return;

    // morph runs with childrenOnly, so the root is left untouched: copy the style
    // attribute across by hand or the dot colour and the hidden-when-empty display
    // would stay on the previous variant's values.
    this.setAttribute('style', newDelivery.getAttribute('style') ?? '');
    this.dataset.state = newDelivery.dataset.state ?? '';
    morph(this, newDelivery, { childrenOnly: true });
  };
}

if (!customElements.get('product-delivery')) {
  customElements.define('product-delivery', ProductDelivery);
}
